import {
    IonButton,
    IonCol,
    IonInput,
    IonItem,
    IonLabel,
    IonRow,
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import { PersonContext } from "../../context/PersonContext";
import { Barn, Event, Horse, S3Object } from "../../models";
import { createHorse, updateHorse } from "../../utilities/horse/Horse";
import ErrorAlert from "../Errors/ErrorAlert";
import HorseHeightInput from "./HorseHeightInput";
import { CreateHorseInput, UpdateHorseInput } from "../../API";
import SelectState from "../Address/SelectState";
import CogginsDocumentForm from "../s3Object/CogginsDocumentForm";
import { createCogginsDocumentFileName, createS3Object, handleUploadS3File } from "../../utilities/s3Object/s3Object";
import moment from "moment";
import RequiredInputIndicator from "../Forms/RequiredInputIndicator";
import Spinner from "../Spinners/Spinner";
import SuccessBanner from "../Banners/SuccessBanner";

interface _Props {
    horse?: (Horse | null)
    selectedBarn?: (Barn | null)
    event?: (Event | null)
    onChange: Function
}

const QuickAddHorseForm: React.FC<_Props> = ({horse, selectedBarn, event, onChange}) => {
    const user = useContext(PersonContext);
    
    const [currentHorse, setCurrentHorse] = useState<Horse | null | undefined>();
    const [name, setName] = useState("");
    const [horseHeightInInches, setHorseHeightInInches] = useState(0);
    const [horseHeightFraction, setHorseHeightFraction] = useState("");
    const [cogginsState, setCogginsState] = useState("");
    const [cogginsNumber, setCogginsNumber] = useState("");
    const [cogginsDate, setCogginsDate] = useState("");
    const [cogginsDocument, setCogginsDocument] = useState<any | null | undefined>();
    const [cogginsS3Object, setCogginsS3Object] = useState<S3Object | null | undefined>();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const getHorseData = (horse?: (Horse | null | undefined)) => {
        setCurrentHorse(horse);
        setName(horse?.name || "");
        setCogginsState(horse?.cogginsState || "");
        setCogginsNumber(horse?.cogginsNumber || "");
        setCogginsDate(horse?.cogginsDate || "");
        setCogginsS3Object(horse?.coggins || null);
        setHorseHeightInInches(horse?.height ? parseFloat(horse?.height) : 0);
        setHorseHeightFraction(horse?.fraction || "");
    }

    if (horse && horse.id !== currentHorse?.id) getHorseData(horse);

    const verifyForm = () => {
        setError("");
        setSuccess("");
        if (!name) {
            setError("Please include a show name for the horse.");
            return false;
        }
        if (event && event.entryOptions?.requiresCogginsData) {
            if (!cogginsDate) {
                setError("Please include a date for the coggins.");
                return false;
            }
            if (cogginsDate && !moment(new Date()).isBefore(moment(cogginsDate).add(1, "year"))) {
                setError("Please make sure the coggins test date is within the last year.");
                return false;
            }
            if (!cogginsNumber) {
                setError("Please include an accession number for the coggins.");
                return false;
            }
            if (!cogginsState) {
                setError("Please include a state for where the coggins test took place.");
                return false;
            }
        }
        if (event && event.entryOptions?.requiresCogginsDocument) {
            if (!cogginsDocument && !cogginsS3Object) {
                setError("Please include a document for the coggins.");
                return false;
            }
        }
        return true;
    }

    const handleHeightChange = (heightInInches: number, fraction: string) => {
        setHorseHeightInInches(heightInInches);
        setHorseHeightFraction(fraction);
    }

    const handleSelectedDocument = (file: any) => {
        setCogginsDocument(file);
    }

    const handleUploadDocument = async (file: any) => {
        if (horse) {
            // Upload this to S3 
            const fileName = createCogginsDocumentFileName(horse, file);
            const uploadResult = await handleUploadS3File(fileName, file);
            if (uploadResult.isSuccess) {
                // If successfully added to S3, save to video list to the person
                const key = uploadResult.result;
                const title = "Coggins for " + horse.name;
                const description = "Coggins uploaded for " + horse.name + " by " + user.firstName + " " + user.lastName + " on " + moment(new Date()).format("MMM DD, YYYY h:mm a"); 
                const s3Object = createS3Object(key, title, file, description);
                return s3Object;
            } else {
                setError("Could not upload the video. Contact hello@ringsidepro.com for help.")
            }
        } else {
            setError("No horse found.")
        }
    }

    const handleCreateHorse = async () => {
        let input: CreateHorseInput = {
            personId: user.id,
            name: name || "",
            ownerId: null,
            ownerName: "",
            height: horseHeightInInches ? horseHeightInInches.toString() : null,
            fraction: horseHeightFraction || "",
            barnId: selectedBarn?.id || null,
            barnName: selectedBarn?.name || null
        };
        if (cogginsState) input.cogginsState = cogginsState;
        if (cogginsDate) input.cogginsDate = cogginsDate;
        if (cogginsNumber) input.cogginsNumber = cogginsNumber;
        if (cogginsDocument) {
            const s3Object = await handleUploadDocument(cogginsDocument);
            input.coggins = s3Object;
        }
        const createResult = await createHorse(input);
        if (createResult.isSuccess) {
            onChange(createResult.result, "create");
            setSuccess("Successfully created the horse.");
        } else {
            setError(createResult.message);
        }
    }

    const handleUpdateHorse = async () => {
        if (horse) {
            let input: UpdateHorseInput = {
                id: horse.id,
                personId: horse.personId || null,
                name: name || null,
                height: horseHeightInInches.toString() || null,
                fraction: horseHeightFraction || null,
                barnId: selectedBarn?.id || null,
                barnName: selectedBarn?.name || null
            };
            if (cogginsState) input.cogginsState = cogginsState;
            if (cogginsDate) input.cogginsDate = cogginsDate;
            if (cogginsNumber) input.cogginsNumber = cogginsNumber;
            if (cogginsDocument) {
                const s3Object = await handleUploadDocument(cogginsDocument);
                input.coggins = s3Object;
            }
            const updateResult = await updateHorse(input);
            if (updateResult.isSuccess) {
                onChange(updateResult.result, "update");
                setSuccess("Successfully updated the horse.");
            } else {
                setError(updateResult.message);
            }
        } else {
            setError("No horse to edit.");
        }
    }

    const handleAddHorse = async () => {
        setIsLoading(true);
        const isValid = verifyForm();
        if (isValid) {
            await handleCreateHorse();
        }
        setIsLoading(false);
    }

    const handleEditHorse = async () => {
        setIsLoading(true);
        const isValid = verifyForm();
        if (isValid) {
            await handleUpdateHorse();
        }
        setIsLoading(false);
    }

    return (
        <>
            {isLoading ?
                <IonRow className="ion-justify-content-center">
                    <IonCol className="ion-text-center">
                        <Spinner />
                    </IonCol>
                </IonRow>
                :
                <>
                    {success && <SuccessBanner width="12" success={success} />}
                    {error && <ErrorAlert width="12" error={error} />}
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">Show Name <RequiredInputIndicator showWordRequired /></IonLabel>
                                <IonInput 
                                    type="text"
                                    value={name}
                                    aria-required={true}
                                    onIonChange={e => {
                                        setName(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="6">
                            <HorseHeightInput isRequired={false} heightInInches={horseHeightInInches} fraction={horseHeightFraction} onChange={handleHeightChange} />
                        </IonCol>
                    </IonRow>
                    {event && event.entryOptions?.requiresCogginsData && (
                        <>
                            <IonRow>
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonItem color="white">
                                        <IonLabel position="stacked">Coggins Accession Number <RequiredInputIndicator showWordRequired /></IonLabel>
                                        <IonInput 
                                            type="text"
                                            value={cogginsNumber}
                                            aria-required={true}
                                            onIonChange={e => {
                                                setCogginsNumber(e.detail.value!);
                                            }}
                                        />
                                    </IonItem>
                                </IonCol>
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonItem color="white">
                                        <IonLabel position="stacked">Coggins Test Date <RequiredInputIndicator showWordRequired /></IonLabel>
                                        <IonInput 
                                            type="date"
                                            value={cogginsDate}
                                            aria-required={true}
                                            onIonChange={e => {
                                                setCogginsDate(e.detail.value!);
                                            }}
                                        />
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol sizeXs="12" sizeMd="6">
                                    <SelectState label="State of Test" isRequired={true} selectedValue={cogginsState} onInputChange={(state: string) => setCogginsState(state)} />
                                </IonCol>
                            </IonRow>
                        </>
                    )}
                    {event && event.entryOptions?.requiresCogginsDocument && (
                        <>
                            <IonRow className="ion-justify-content-center">
                                <IonCol size="12">
                                    <CogginsDocumentForm horse={horse} onSubmit={handleSelectedDocument} />
                                </IonCol>
                            </IonRow>
                        </>
                    )}
                    <IonRow className="ion-justify-content-center">
                        <IonCol sizeMd="4" className="ion-text-center">
                            <IonButton
                                className="ion-margin-top"
                                color="tertiary"
                                expand="block"
                                onClick={horse ? handleEditHorse : handleAddHorse}
                            >
                                {horse ? "Edit " + horse.name : "Add Horse"}
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </>
            }
        </>
    );
};

export default QuickAddHorseForm;